<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
    import { __, getLanguageDirection } from '@oat-sa-private/ui-core';
    import Icon from '../icon/Icon.svelte';

    export let validity = { messages: [], overallValidity: true };
    export let showInvalidFeedback = true;
    export let firstTry = true; // behaviour differs after first blur
    export let feedbackAnnounceTimeout = 500;
    export let lang;

    // cast prop data:
    if (!Array.isArray(validity.messages)) {
        validity.messages = [];
    }
    validity.overallValidity = Boolean(validity.overallValidity);

    $: validity && setFeedbackAnnounce();
    $: invalidValidityMessages = validity?.messages?.filter(line => !line.valid) ?? [];
    $: showFeedback = showInvalidFeedback;
    $: dir = lang ? getLanguageDirection(lang) : void 0;

    let feedbackAnnounceTimer;
    let feedbackAnnounce = false;

    /**
     * Debounces SR announce with timer
     */
    function setFeedbackAnnounce() {
        clearTimeout(feedbackAnnounceTimer);
        feedbackAnnounce = false;
        feedbackAnnounceTimer = setTimeout(() => {
            feedbackAnnounce = true;
        }, feedbackAnnounceTimeout);
    }

    /**
     * Check if specific invalid feedback should be hidden
     * @param {ValidityMessage} line
     * @returns {Boolean}
     */
    function isInvalidLineHidden(line) {
        return (
            typeof showInvalidFeedback === 'object' &&
            line.type &&
            line.type in showInvalidFeedback &&
            !showInvalidFeedback[line.type]
        );
    }

    /**
     * Check if specific feedback should be hidden
     * @param {ValidityMessage} line
     * @returns {Boolean}
     */
    function isLineHidden(line) {
        return !line.valid && isInvalidLineHidden(line);
    }
</script>

<style>
    .feedback-block {
        display: block;

        & > .invalid {
            color: var(--color-alert);
        }
    }

    .feedback-message {
        display: flex;
        align-items: flex-start;
        margin-top: var(--space-half);
    }

    .feedback-block.hide-invalid .invalid,
    .feedback-block .invalid.hide-invalid,
    .feedback-block .valid.hide-valid {
        display: none;
    }

    .icon-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 1em;
        width: 1em;
        margin-top: 0.2em;
        margin-right: 0.2em;
    }
</style>

{#if showFeedback}
    <span
        class="feedback-block"
        class:hide-invalid={!showInvalidFeedback}
        aria-live="assertive"
        aria-relevant="all"
        {lang}
        {dir}>
        {#if invalidValidityMessages}
            {#each invalidValidityMessages as line}
                <span
                    aria-hidden="true"
                    class={`feedback-message ${line.valid || firstTry ? 'valid' : 'invalid'}`}
                    class:hide-invalid={isInvalidLineHidden(line, showInvalidFeedback)}>
                    <span class="icon-wrapper">
                        <Icon name="lightning-16" />
                    </span>
                    <span> {line.msg} </span>
                </span>
                {#if feedbackAnnounce && !isLineHidden(line, showInvalidFeedback)}
                    <span class="visually-hidden"> {line.valid ? __('valid:') : __('invalid:')} {line.msg} </span>
                {/if}
            {/each}
        {/if}
    </span>
{/if}
